import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { ChannelContextService } from '@x/ecommerce-shop/app/channel/services/channel-context.service';
import { ChannelState } from '@x/ecommerce-shop/app/channel/state';
import { AddressService } from '@x/ecommerce-shop/app/core/address/services/address.service';
import { ShopUserSubscriberService } from '@x/ecommerce-shop/app/core/auth/services/shop-user-subscriber.service';
import { UserAddressDialogService } from '@x/ecommerce-shop/app/core/auth/services/user-address-dialog.service';
import {
  AuthCreateUserAddress,
  AuthDeleteUserAddress,
  AuthRefreshUserAddresses,
  AuthSetDefaultUserAddress,
  AuthUpdateUserAddress,
} from '@x/ecommerce-shop/app/core/auth/state/auth-address.actions';
import { AuthState } from '@x/ecommerce-shop/app/core/auth/state/auth.state';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import {
  IShopAddress,
  IShopUserAddress,
  IShopUserSubscriber,
  ShopUserService,
} from '@x/ecommerce/shop-client';
import { firstValueFrom, lastValueFrom, of, switchMap } from 'rxjs';

export interface IAddressCard {
  addressId: number;
  isDefault: boolean;
  cardHolder?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  businessName?: string | null;
  complex?: string | null;
  street?: string | null;
  suburb?: string | null;
  city?: string | null;
  postalCode?: string | null;
  subscribers: IShopUserSubscriber[];
  alias?: string | null;
  instructions?: string | null;
  channelCode: string;
  userId: number;
}

@Injectable()
export class ShopUserAddressService {
  private get userAddresses(): IShopUserAddress[] {
    return this.store.selectSnapshot(AuthState.userAddresses);
  }

  private get user() {
    return this.store.selectSnapshot(AuthState.loggedInUser);
  }

  private get subscribers(): IShopUserSubscriber[] {
    return this.store.selectSnapshot(AuthState.managedSubscribers);
  }

  private get defaultCountry() {
    return this.store.selectSnapshot(ChannelState.activeChannel)?.defaultCountry;
  }

  constructor(
    private store: Store,
    private userAddressDialogService: UserAddressDialogService,
    private channelContext: ChannelContextService,
    private addressService: AddressService,
    private shopUserService: ShopUserService,
    private shopUserSubscriberService: ShopUserSubscriberService,
    @Inject(SHOP_CONFIG) private shopConfig: IShopConfig,
  ) {}

  makeAddressCard(address: IShopUserAddress | IShopAddress): IAddressCard {
    const subscribers = this.subscribers;
    const userId = this.validateAndGetUser().id;
    const channelCode = this.validateAndGetChannelCode();
    const cardWithSubscribers = subscribers.filter(
      (subscriber) => subscriber.shippingAddress?.hash === address.hash,
    );

    return {
      addressId: address.id,
      isDefault: Boolean(address.isDefaultShippingAddress || address.isDefaultBillingAddress),
      cardHolder: `${address.firstName}${address.lastName ? ` ${address.lastName}` : ''}`,
      firstName: address.firstName,
      lastName: address.lastName,
      businessName: address.businessName,
      complex: address.complex,
      street: address.street,
      suburb: address.suburb,
      city: address.city,
      postalCode: address.postalCode,
      alias: address.alias,
      instructions: address.instructions,
      subscribers: cardWithSubscribers,
      channelCode,
      userId,
    };
  }

  async deleteAddress$(addressCard: IAddressCard) {
    const { addressId, subscribers, userId, channelCode } = addressCard;

    if (subscribers.length > 0 || addressCard.isDefault) {
      const newAddress = await this.handleAddressDialog(
        subscribers,
        this.getUserAddressesExcluding(addressId),
        userId,
        channelCode,
      );

      const subscriberIds = this.getSubscriberIds(subscribers);

      if (!newAddress) return;

      await this.shopUserSubscriberService.updateSubscribersWithUserAddress(
        subscriberIds,
        newAddress,
      );

      // If this was the default address, set the new address as default
      if (addressCard.isDefault) {
        await this.setDefaultAddress(newAddress.id, channelCode);
      }
    }

    return await firstValueFrom(this.store.dispatch(new AuthDeleteUserAddress({ addressId })));
  }

  async toggleDefaultAddress$(makeDefault: boolean, addressCard: IAddressCard) {
    const { addressId } = addressCard;

    if (makeDefault) {
      return this.makeNewDefault$(addressId, addressCard.channelCode);
    }

    return this.unSetDefault$(addressCard);
  }

  async editAddress$(addressCard: IAddressCard): Promise<void> {
    const address = this.userAddresses.find((a) => a.id === addressCard.addressId);

    if (!address) return;

    const addressInput = await lastValueFrom(
      this.addressService
        .validAddress$(
          {
            address: this.addressService.getAddressInputFromShopAddress(address),
            sheetTitle: 'Edit Address',
            user: this.validateAndGetUser(),
            defaultCoordinates: this.shopConfig.googleMaps.defaultCoordinates,
            ...(this.defaultCountry && { defaultCountry: this.defaultCountry }),
            origin: 'My Account',
            mode: 'update',
          },
          [],
        )
        .pipe(switchMap((operation) => of(operation.data))),
    );

    if (!addressInput) return;

    return await firstValueFrom(
      this.store.dispatch(
        new AuthUpdateUserAddress({
          addressId: addressCard.addressId,
          address: addressInput,
        }),
      ),
    );
  }

  async createAddress$(): Promise<void> {
    const newAddress = await lastValueFrom(
      this.addressService
        .validAddress$(
          {
            sheetTitle: 'Add address',
            user: this.validateAndGetUser(),
            defaultCoordinates: this.shopConfig.googleMaps.defaultCoordinates,
            ...(this.defaultCountry && { defaultCountry: this.defaultCountry }),
            origin: 'My Account',
            mode: 'update',
          },
          [],
        )
        .pipe(switchMap((operation) => of(operation.data))),
    );

    if (!newAddress) return;

    return await firstValueFrom(
      this.store.dispatch(
        new AuthCreateUserAddress({
          address: newAddress,
        }),
      ),
    );
  }

  private async unSetDefault$(addressCard: IAddressCard) {
    const { addressId, subscribers, channelCode, userId } = addressCard;
    const userAddresses = this.getUserAddressesExcluding(addressId);
    const newAddress = await this.handleAddressDialog(
      subscribers,
      userAddresses,
      userId,
      channelCode,
    );

    if (newAddress) {
      const subscriberIds = this.getSubscriberIds(subscribers);
      await this.shopUserSubscriberService.updateSubscribersWithUserAddress(
        subscriberIds,
        newAddress,
      );
      return await this.setDefaultAddress(newAddress.id, channelCode);
    }

    return firstValueFrom(
      this.store.dispatch(new AuthRefreshUserAddresses(addressCard.channelCode)),
    );
  }

  private async makeNewDefault$(addressId: number, channelCode: string) {
    const newDefaultAddress = this.validateAndFindUserAddress(addressId);

    const currentDefaultAddress = this.userAddresses.find(
      (addr) => addr.isDefaultShippingAddress || addr.isDefaultBillingAddress,
    );

    let transferSubscribers: IShopUserSubscriber[] = [];

    if (currentDefaultAddress) {
      transferSubscribers = this.subscribers.filter(
        (subscriber) => subscriber.shippingAddress?.hash === currentDefaultAddress.hash,
      );
    }

    if (transferSubscribers.length) {
      const response = await firstValueFrom(
        this.userAddressDialogService.setDefaultAddressDialog$({
          subscribers: transferSubscribers,
        }),
      );

      if (!response) {
        await firstValueFrom(this.store.dispatch(new AuthRefreshUserAddresses(channelCode)));
        return;
      }

      if (response.length) {
        await this.shopUserSubscriberService.updateSubscribersWithUserAddress(
          response,
          newDefaultAddress,
        );
      }
    }

    return await this.setDefaultAddress(newDefaultAddress.id, channelCode);
  }

  private async handleAddressDialog(
    subscribers: IShopUserSubscriber[],
    userAddresses: IShopUserAddress[],
    userId: number,
    channelCode: string,
  ): Promise<IShopUserAddress | undefined> {
    const response = await firstValueFrom(
      this.userAddressDialogService.chooseDefaultAddressDialog$({
        user: this.user,
        defaultCountry: this.defaultCountry,
        subscribers,
        userAddresses,
      }),
    );

    if (response?.newAddressInput) {
      return await firstValueFrom(
        this.shopUserService.createUserAddress({
          input: {
            address: response.newAddressInput,
            userId,
            channelCode,
          },
        }),
      );
    } else if (response?.existingAddressId) {
      return this.userAddresses.find((a) => a.id === response.existingAddressId);
    }

    return undefined;
  }

  private async setDefaultAddress(addressId: number, channelCode: string): Promise<void> {
    await firstValueFrom(
      this.store.dispatch(
        new AuthSetDefaultUserAddress({
          input: {
            addressId,
            channelCode,
          },
        }),
      ),
    );
  }

  private getUserAddressesExcluding(excludeId: number): IShopUserAddress[] {
    return this.userAddresses.filter((address) => address.id !== excludeId);
  }

  private validateAndFindUserAddress(addressId: number): IShopUserAddress {
    const address = this.userAddresses.find((a) => a.id === addressId);
    if (!address) throw new Error('User address does not exist');
    return address;
  }

  private validateAndGetUser() {
    const user = this.user;
    if (!user) throw new Error('User is not logged in');
    return user;
  }

  private validateAndGetChannelCode(): string {
    const channelCode = this.channelContext.channelCode;
    if (!channelCode) throw new Error('Channel code is not set');
    return channelCode;
  }

  private getSubscriberIds(subscribers: IShopUserSubscriber[]): number[] {
    return subscribers.map(({ id }) => id);
  }
}
